import React, { useContext, useState, useEffect } from "react";
import { Modal, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    Button,
    Typography,
} from "@mui/material";
import TextField from '@mui/material/TextField';
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useSelector } from "react-redux";
import AutoCompleteDropdown from "components/autocomplete/AutoCompleteDropdown";
import { MissionContext } from "../contexts/MissionTasksContext";
import { displayNotification, sendAPIRequest } from "helpers";
import TextFieldCustom from "components/TextFieldCustom";
import { FormDataContext } from "../contexts/FormContext";

const ReportModal = ({ isShow, initModal, missionId, onSubmitSuccess }) => {
    const { t } = useTranslation();
    const { missionData } = useContext(MissionContext);
    const authData = useSelector((state) => state.auth.data);
    const [forms, setForms] = useState([])
    const [ranks, setRanks] = useState([])
    const [allFormsData, setAllFormsData] = useState([]);
    const formDataContext = useContext(FormDataContext);

    const [formValues, setFormValues] = useState({
        reason: "",
        missionType: "",
        feature: "Workflow",
        description: "",
        attachment: null,
        user: "",
        formName: "",
        rank: "",
        mission_id: missionId
    });

    const reasonOptions = [
        { label: t("Select Reason"), value: "" },
        { label: t("Report A Bug/ Issue"), value: "Report A Bug/ Issue" },
        { label: t("Ask a Question"), value: "Ask a Question" },
    ];
    let getFormsData = async () => {
        try {
            const req = await sendAPIRequest(`/api/pvsa/forms`);
            const ret = await req.json();
            if (ret.success) {
                const filteredForms = ret.data.filter(form => Number(form.type) !== 384);
                filteredForms.forEach(form => {
                    const formOptions = filteredForms.map(form => ({
                        label: form.name,
                        value: form.name,
                    }));
                    setForms(formOptions);

                });
                setAllFormsData(filteredForms);
            } else {
                displayNotification("error", "Error loading Form Data [B]");
            }
        } catch (error) {
            console.error("Submit error:", error);
            alert(t("An error occurred"));
        }
    }
    useEffect(() => {
        if (missionData?.mission_type_name) {
            setFormValues(prev => ({
                ...prev,
                missionType: missionData.mission_type_name
            }));
        }
    }, [missionData]);
    useEffect(() => {
        if (isShow) {
            setFormValues({
                reason: "",
                missionType: missionData?.mission_type_name || "",
                feature: "Workflow",
                description: "",
                attachment: null,
                user: authData?.name || "",
                formName: "",
            });
        }
    }, [isShow]);
    useEffect(() => {
        getFormsData()
    }, []);
    const handleChange = (field, value) => {
        setFormValues(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAttachmentChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleChange("attachment", file);
        }
    };

    const handleSubmit = async () => {
        const { reason, feature, description, missionType, attachment, formName, rank } = formValues;

        if (!reason || !description) {
            alert(t("Please fill in all required fields"));
            return;
        }
        const formData = new FormData();
        formData.append("issue_type", reason);
        formData.append("feature", feature);
        formData.append("description", description);
        formData.append("mission_type", missionType);
        formData.append("user_id", authData?.id);
        formData.append("formName", formName);
        formData.append("mission_id", missionId);

        if (attachment) {
            formData.append("attachment", attachment);
        }
        try {
            const ret = await sendAPIRequest(`/api/reported-issues`, "POST", formData);
            const res = await ret.json();
            if (res.success) {
                displayNotification("success", res.message);
                if (onSubmitSuccess) onSubmitSuccess(); 
                initModal();
            } else {
                displayNotification("error", res.message);
            }
        } catch (error) {
            console.error("Submit error:", error);
            alert(t("An error occurred"));
        }
    };

    return (
        <Modal show={isShow} onHide={initModal}>
            <Modal.Header closeButton>
                <Modal.Title>{t("Raise An Issue")}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <Form.Group controlId="reportReason">
                    <AutoCompleteDropdown
                        label={t("Select Issue type")}
                        multiple={false}
                        options={reasonOptions}
                        value={reasonOptions.find((option) => option.value === formValues.reason) || null}
                        onChange={(e, value) => handleChange("reason", value?.value || "")}
                    />
                </Form.Group>

                {formValues.reason && (
                    <>
                        <Form.Group controlId="reportUser" style={{ display: 'none' }}>
                            <AutoCompleteDropdown
                                label={t("Select User")}
                                multiple={false}
                                options={[{ label: formValues.user, value: formValues.user }]}
                                value={{ label: formValues.user, value: formValues.user }}
                                onChange={() => { }}
                                disabled={true}
                            />
                        </Form.Group>

                        <Form.Group style={{ display: 'none' }}>
                            <AutoCompleteDropdown
                                label={t("Select Mission Type")}
                                multiple={false}
                                options={[{ label: formValues.missionType, value: formValues.missionType }]}
                                value={{ label: formValues.missionType, value: formValues.missionType }}
                                disabled={true}
                                onChange={() => { }}
                            />
                        </Form.Group>

                        <Form.Group controlId="formName">
                            <AutoCompleteDropdown
                                label={t("Form Name")}
                                multiple={false}
                                options={forms}
                                value={forms.find((option) => option.value === formValues.formName) || null}
                                onChange={(e, value) => handleChange("formName", value?.value)}
                            />
                        </Form.Group>
                        <div className="col-12 mb-4 py-2">
                            <TextFieldCustom
                                id="outlined-multiline-flexible"
                                label={t("-1", "Description")}
                                multiline
                                rows={5}
                                value={formValues.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                                fullWidth
                                size="small"
                                error={false}
                            />
                        </div>
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<CloudUploadIcon />}
                            fullWidth
                            sx={{ mt: 2 }}
                        >
                            {t("Upload Attachment")}
                            <input type="file" hidden onChange={handleAttachmentChange} />
                        </Button>

                        {formValues.attachment && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                {t("Selected")}: {formValues.attachment.name}
                            </Typography>
                        )}
                    </>
                )}
            </Modal.Body>

            <Modal.Footer>
                <button className="btn btn--outline fs-7 lh-1 mb-2" onClick={initModal}>
                    {t("Cancel")}
                </button>
                <button
                    className="btn btn--primary fs-7 lh-1 mb-2 ms-1"
                    onClick={handleSubmit}
                >
                    {t("Submit")}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

export default ReportModal;
